import React, { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Brain } from 'lucide-react';
import { useVoice } from '../context/VoiceContext';
import { useAuth } from '../context/AuthContext';

/**
 * GlobalWakeWordListener
 * 
 * This component listens for the wake word globally and shows a visual indicator
 * when detected. It can navigate the user to the voice agent or trigger voice input
 * on the current page.
 */
export default function GlobalWakeWordListener() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { 
    alwaysListening, 
    wakeWordDetected, 
    isWakeWordListening,
    onWakeWordDetected,
    wakeWord
  } = useVoice();

  // Handle wake word detection
  const handleWakeWordDetected = useCallback((transcript) => {
    console.log('Global wake word handler triggered:', transcript);
    
    // If already on voice page, the page will handle it
    if (location.pathname === '/voice' || location.pathname === '/assistant') {
      // The voice page will pick up from the context
      return;
    }
    
    // Navigate to voice agent if authenticated
    if (isAuthenticated) {
      navigate('/voice');
    } else {
      navigate('/login?redirect=/voice');
    }
  }, [navigate, location.pathname, isAuthenticated]);

  // Register the wake word callback
  useEffect(() => {
    if (alwaysListening) {
      onWakeWordDetected(handleWakeWordDetected);
    }
  }, [alwaysListening, onWakeWordDetected, handleWakeWordDetected]);

  // Don't render anything if not listening
  if (!alwaysListening) return null;

  return (
    <>
      {/* Floating Wake Word Indicator */}
      <AnimatePresence>
        {isWakeWordListening && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-4 left-4 z-40"
          >
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-md border ${
              wakeWordDetected
                ? 'bg-green-500/20 border-green-500/30'
                : 'bg-black/40 border-white/10'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                wakeWordDetected 
                  ? 'bg-green-500' 
                  : 'bg-accent animate-pulse'
              }`} />
              <Mic className={`w-4 h-4 ${
                wakeWordDetected ? 'text-green-400' : 'text-accent'
              }`} />
              <span className={`text-xs ${
                wakeWordDetected ? 'text-green-400' : 'text-gray-400'
              }`}>
                {wakeWordDetected 
                  ? 'Activated!' 
                  : `Say "${wakeWord}"`
                }
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Full Screen Wake Word Detection Overlay */}
      <AnimatePresence>
        {wakeWordDetected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm pointer-events-none"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="text-center"
            >
              {/* Animated Brain Icon */}
              <motion.div
                animate={{ 
                  scale: [1, 1.1, 1],
                  boxShadow: [
                    '0 0 30px rgba(56, 189, 248, 0.3)',
                    '0 0 60px rgba(139, 92, 246, 0.5)',
                    '0 0 30px rgba(56, 189, 248, 0.3)'
                  ]
                }}
                transition={{ duration: 1, repeat: Infinity }}
                className="w-24 h-24 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center mx-auto mb-4"
              >
                <Brain className="w-12 h-12 text-white" />
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-2xl font-heading font-bold text-white mb-2"
              >
                AIRA Activated
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-gray-300"
              >
                Listening for your command...
              </motion.p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
